// Mapa en vivo de los choferes. El GPS del PR 1 junta 77.000 posiciones por
// día y hasta ahora ninguna pantalla las mostraba: acá está la última posición
// de cada chofer, con su estado de seguimiento y hace cuánto reportó.
//
// Lo que un despachante necesita leer de un vistazo no es dónde está cada
// punto sino QUIÉN DEJÓ DE REPORTAR: un chofer en gris hace más de cinco
// minutos que no manda nada, y eso es lo primero que hay que mirar.
//
// Leaflet se carga en tiempo de ejecución desde el CDN, sin dependencia de
// npm: los mapas base ya vienen de internet (OpenStreetMap), así que el panel
// no gana nada empaquetando la librería y sí evita tocar el package-lock.
//
// Al estilo My Maps (lo que usaba la operación): en «Capas» se prende y se
// apaga cada cosa (colectas y repartos del día por estado, depósitos,
// tiendas, zonas y domicilios); a la derecha, «Ver:» elige qué se lista, con
// búsqueda y filtros. Mapa y lista van juntos: lo que se toca en uno se marca
// en el otro y abre su ficha. «Ver su recorrido» deja en el mapa solo lo de
// un chofer: sus paradas en orden, el trazado por calles y su domicilio.
// Arriba del mapa, vistas armadas («Colectas de hoy», «Repartos de hoy»…)
// prenden las capas y eligen la lista de una vez. Al alejar el zoom, los
// puntos que caen juntos se agrupan en un círculo con la cantidad.
// Lo elegido queda guardado en el navegador (ver lib/mapLayers).
// Abajo, el horario del GPS: fuera de esa franja la app apaga el seguimiento.
//
// Dónde está cada cosa: los datos vienen de hooks (usePolledJson, que
// descarta respuestas viejas; useDriverHomes; useFocoTrazados), el mapa de
// useLeafletMap, el dibujo de cada capa de lib/mapDraw y lo que se lee de
// una posición de lib/liveDrivers. Las partes de la pantalla (capas, fila de
// chofer, ficha, números, referencias) están en components/mapaChoferes.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { subtitleStyle, titleStyle } from "../components/ui/pageStyles";
import DriverRow from "../components/mapaChoferes/DriverRow";
import Ficha from "../components/mapaChoferes/Ficha";
import GpsFranjaCard from "../components/mapaChoferes/GpsFranjaCard";
import KpiStrip from "../components/mapaChoferes/KpiStrip";
import LayersMenu from "../components/mapaChoferes/LayersMenu";
import Legend from "../components/mapaChoferes/Legend";
import { capaStyle, chipFocoStyle, cerrarStyle, filaStyle, filaTituloStyle, linkBtnStyle, punto, searchStyle, selectStyle, smallMutedStyle } from "../components/mapaChoferes/styles";
import { useAuth } from "../context/AuthContext";
import { useDriverHomes } from "../hooks/useDriverHomes";
import { useFocoTrazados } from "../hooks/useFocoTrazados";
import { useLeafletMap } from "../hooks/useLeafletMap";
import { usePolledJson } from "../hooks/usePolledJson";
import type { LatLng } from "../lib/leafletTypes";
import { Chofer, llevaTurbo, nombreChofer, ordenarChoferes, resumenChoferes, SIN_SENAL_MIN } from "../lib/liveDrivers";
import {
  crearGrupos, dibujarChoferes, dibujarDepositos, dibujarDomicilios, dibujarFoco, dibujarMarca, dibujarPuntos, dibujarTurbos, dibujarZonas, Pick, subirChoferes,
} from "../lib/mapDraw";
import {
  CLUSTER_MAX_ZOOM, DEFAULT_FILTERS, DRIVER_FILTERS, DriverFilter, LAYERS, LayerKey, LayerPrefs, ListFilters, MapLayersData, MapSel, STATE_LABEL, StopState, TurboHoy, VIEWS, ViewKey,
  applyView, contarElegidos, currentView, driverStops, filterDrivers, homeName, loadPrefs, normalize, rowsFor, savePrefs, stopCounts, stopDrivers, stopRow, zoneCenter,
} from "../lib/mapLayers";
import { requestJson } from "../lib/requestJson";
import { fmtKm } from "../lib/streetRouting";

const REFRESCO_MS = 10_000;
// Las paradas cambian de estado más despacio que las posiciones.
const REFRESCO_CAPAS_MS = 60_000;
// Los Turbos del día (tiendas con Turbo y destinos), al ritmo de «Hoy en vivo».
const REFRESCO_TURBOS_MS = 15_000;
// Canvas: con todas las capas prendidas son miles de puntos (las entregas
// del día) y como elementos sueltos el mapa se arrastra.
const MAPA = { center: [-34.62, -58.45] as LatLng, zoom: 10, preferCanvas: true };

const SIN_CHOFERES: Chofer[] = [];
const leerChoferes = (json: any): Chofer[] => json.drivers ?? [];
const leerCapas = (json: any): MapLayersData => json;
const SIN_TURBOS: TurboHoy[] = [];
const leerTurbos = (json: any): TurboHoy[] => (Array.isArray(json?.turbos) ? json.turbos : []);
const AGRUPABLES: LayerKey[] = ["stores", "pickups", "deliveries"];

type Sel = MapSel | null;

export default function MapaChoferesPage() {
  const { token, capabilities } = useAuth();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState<LayerPrefs>(loadPrefs);
  const [soloActivos, setSoloActivos] = useState(false);
  const ubicaciones = usePolledJson(`/v2/driver-locations/last${soloActivos ? `?active_minutes=${SIN_SENAL_MIN}` : ""}`, REFRESCO_MS, token, leerChoferes);
  const choferes = ubicaciones.data ?? SIN_CHOFERES;
  const capasVivas = usePolledJson("/v2/ops/map-layers", REFRESCO_CAPAS_MS, token, leerCapas);
  const capas = capasVivas.data;
  // Los Turbos salen de /v2/turbo/today, que pide db_access: sin el permiso la
  // capa no se ofrece ni se pide (daba 403 cada 15 s y se veía «0»). Tampoco
  // se pide con la capa apagada y la lista en otra cosa.
  const verTurbo = capabilities?.db_access === true;
  const pedirTurbos = verTurbo && (prefs.visible.turbo || prefs.view === "turbo");
  const turbosVivos = usePolledJson(pedirTurbos ? "/v2/turbo/today" : null, REFRESCO_TURBOS_MS, token, leerTurbos);
  const turbos = turbosVivos.data ?? SIN_TURBOS;
  // null: todavía no se sabe; false: el usuario no tiene permiso para verlos.
  const domicilios = useDriverHomes(token);
  const [aviso, setAviso] = useState<{ driverId: number; texto: string; ok: boolean } | null>(null);
  const [avisando, setAvisando] = useState<number | null>(null);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const listaRef = useRef<HTMLDivElement>(null);
  const [sel, setSel] = useState<Sel>(null);
  const [filtros, setFiltros] = useState<ListFilters>(DEFAULT_FILTERS);
  const [menuAbierto, setMenuAbierto] = useState(false);
  // Chofer en foco (driver_data.id): el mapa muestra solo lo suyo.
  const [foco, setFoco] = useState<number | null>(null);
  const encuadrado = useRef<number | null>(null);
  // El zoom del mapa: el agrupado de puntos depende de él.
  const [zoom, setZoom] = useState(MAPA.zoom);

  // El mapa se crea una vez, con un grupo de capas por cosa (lib/mapDraw);
  // al salir de la pantalla se destruye (hooks/useLeafletMap).
  const { ready: leaflet, leafletMap } = useLeafletMap(contenedorRef, MAPA, (L, map) => {
    map.on("zoomend", () => setZoom(map.getZoom()));
    return crearGrupos(L, map);
  });

  const cambiarPrefs = (cambio: (p: LayerPrefs) => LayerPrefs) =>
    setPrefs((p) => { const n = cambio(p); savePrefs(n); return n; });
  const alternarCapa = (k: LayerKey) => cambiarPrefs((p) => ({ ...p, visible: { ...p.visible, [k]: !p.visible[k] } }));
  const alternarEstado = (s: StopState) => cambiarPrefs((p) => ({ ...p, states: { ...p.states, [s]: !p.states[s] } }));
  // Elegir qué ver en el panel prende esa capa: lo que se lista, se ve.
  const elegirVista = (k: LayerKey) => {
    cambiarPrefs((p) => ({ ...p, view: k, visible: { ...p.visible, [k]: true } }));
    setFiltros(DEFAULT_FILTERS);
    setFoco(null);
  };
  const aplicarVista = (k: ViewKey) => {
    cambiarPrefs((p) => applyView(p, k));
    setFiltros(DEFAULT_FILTERS);
    setFoco(null);
    setSel(null);
  };

  const homes = useMemo(() => domicilios || [], [domicilios]);
  const stops = useMemo(() => capas?.stops ?? [], [capas]);
  // Los choferes que se ven, en el mapa y en la lista, según el filtro.
  const choferesVisibles = useMemo(() => filterDrivers(choferes, prefs.driverFilter, prefs.chosen), [choferes, prefs.driverFilter, prefs.chosen]);
  const elegirFiltro = (k: DriverFilter) => cambiarPrefs((p) => ({ ...p, driverFilter: k }));
  const alternarElegido = (id: number) =>
    cambiarPrefs((p) => ({ ...p, chosen: p.chosen.includes(id) ? p.chosen.filter((x) => x !== id) : [...p.chosen, id] }));
  const elegirVarios = (ids: number[]) => cambiarPrefs((p) => ({ ...p, chosen: ids, driverFilter: ids.length ? "CHOSEN" : p.driverFilter }));

  // Dónde está lo elegido, sea lo que sea.
  const ubicar = useCallback((s: Sel): LatLng | null => {
    if (!s) return null;
    const par = (lat: number | null | undefined, lng: number | null | undefined): LatLng | null => (lat != null && lng != null ? [lat, lng] : null);
    if (s.kind === "drivers") { const c = choferes.find((x) => x.driver_id === s.id); return par(c?.latitude, c?.longitude); }
    if (s.kind === "pickups" || s.kind === "deliveries") { const x = stops.find((p) => p.id === s.id); return par(x?.latitude, x?.longitude); }
    if (s.kind === "stores") { const x = capas?.stores.find((p) => p.id === s.id); return par(x?.latitude, x?.longitude); }
    if (s.kind === "warehouses") { const x = capas?.warehouses.find((p) => p.id === s.id); return par(x?.latitude, x?.longitude); }
    if (s.kind === "zones") { const z = capas?.zones.find((p) => p.id === s.id); return z ? zoneCenter(z) : null; }
    if (s.kind === "homes") { const h = homes.find((p) => p.driver_id === s.id); return par(h?.latitude, h?.longitude); }
    if (s.kind === "turbo") { const t = turbos.find((p) => p.delivery_id === s.id); const p = t?.destino ?? t?.tienda; return par(p?.lat, p?.lng); }
    return null;
  }, [choferes, stops, capas, homes, turbos]);

  // Elegir algo: desde la lista centra el mapa; desde el mapa, además, el
  // panel pasa a la lista de ese tipo (sin filtros, para que la fila esté).
  const seleccionar = (s: Sel, origen: "lista" | "mapa") => {
    setSel(s);
    if (!s) return;
    if (origen === "mapa" && foco == null && s.kind !== prefs.view) {
      cambiarPrefs((p) => ({ ...p, view: s.kind }));
      setFiltros(DEFAULT_FILTERS);
    }
    const pos = ubicar(s);
    const mapa = leafletMap?.map;
    if (origen === "lista" && pos && mapa) mapa.setView(pos, Math.max(mapa.getZoom(), 14));
  };
  // Los handlers de Leaflet se crean una vez por dibujo: leen la última
  // versión de `seleccionar` por la ref, y `pick` no cambia nunca (así no
  // obliga a redibujar).
  const seleccionarRef = useRef(seleccionar);
  seleccionarRef.current = seleccionar;
  const pick = useCallback<Pick>((s) => () => seleccionarRef.current(s, "mapa"), []);

  // La fila elegida queda a la vista en la lista.
  useEffect(() => {
    if (!sel) return;
    const fila = listaRef.current?.querySelector(`[data-fila="${sel.kind}:${sel.id}"]`) as HTMLElement | null;
    fila?.scrollIntoView?.({ block: "nearest" });
  }, [sel, prefs.view, foco]);

  const verRecorrido = (driverId: number) => {
    setFoco(driverId);
    setSel(null);
    setMenuAbierto(false);
  };

  // Prender y apagar capas (también al crearse el mapa, con lo guardado). Con
  // un chofer en foco se apagan todas: queda solo lo suyo.
  useEffect(() => {
    if (!leafletMap) return;
    const { map, extra: g } = leafletMap;
    for (const l of LAYERS) {
      const grupo = g[l.key];
      const on = foco == null && prefs.visible[l.key] && (l.key !== "homes" || !!domicilios);
      if (on && !map.hasLayer(grupo)) grupo.addTo(map);
      if (!on && map.hasLayer(grupo)) map.removeLayer(grupo);
    }
    // Una capa recién prendida queda arriba: los choferes vuelven a subir.
    subirChoferes(g);
  }, [leafletMap, prefs.visible, domicilios, foco]);

  // Capas fijas del día que no se agrupan: zonas y depósitos.
  useEffect(() => {
    if (!leafletMap) return;
    const { L, extra: g } = leafletMap;
    dibujarZonas(L, g, capas, pick);
    dibujarDepositos(L, g, capas, pick);
    subirChoferes(g);
  }, [leafletMap, capas, pick]);

  // Tiendas y paradas. Son miles de puntos: el zoom y lo elegido solo cuentan
  // mientras se agrupa (si no, cada zoomend o cada toque, aunque sea a un
  // chofer, los volvía a crear todos). Lo elegido va como clave de texto: un
  // toque al mismo punto no redibuja.
  const agrupar = prefs.cluster && zoom < CLUSTER_MAX_ZOOM;
  const zoomAgrupado = agrupar ? zoom : null;
  const selAgrupado = agrupar && sel && AGRUPABLES.includes(sel.kind) ? `${sel.kind}:${sel.id}` : null;
  useEffect(() => {
    if (!leafletMap) return;
    const { L, map, extra: g } = leafletMap;
    const [kind, id] = selAgrupado ? selAgrupado.split(":") : [];
    const agrupado = zoomAgrupado == null ? null : { zoom: zoomAgrupado, sel: kind ? { kind: kind as LayerKey, id: Number(id) } : null };
    dibujarPuntos(L, map, g, capas, prefs.states, agrupado, pick);
    subirChoferes(g);
  }, [leafletMap, capas, prefs.states, zoomAgrupado, selAgrupado, pick]);

  useEffect(() => {
    if (leafletMap) dibujarDomicilios(leafletMap.L, leafletMap.extra, homes, pick);
  }, [leafletMap, homes, pick]);

  useEffect(() => {
    if (leafletMap) dibujarChoferes(leafletMap.L, leafletMap.extra, choferesVisibles, pick);
  }, [leafletMap, choferesVisibles, pick]);

  useEffect(() => {
    if (!leafletMap) return;
    dibujarTurbos(leafletMap.L, leafletMap.extra, turbos, pick);
    subirChoferes(leafletMap.extra);
  }, [leafletMap, turbos, pick]);

  /* ===== Foco en un chofer: sus paradas en orden, su trazado y su casa ===== */

  const paradasFoco = useMemo(() => (foco == null ? [] : driverStops(stops, foco)), [foco, stops]);
  const choferFoco = foco == null ? null : choferes.find((c) => c.driver_id === foco) ?? null;
  const casaFoco = foco == null ? null : homes.find((h) => h.driver_id === foco) ?? null;
  const nombreFoco = foco == null ? "" :
    (choferFoco ? nombreChofer(choferFoco) : null) ?? paradasFoco[0]?.driver_name ?? paradasFoco[0]?.driver_username ?? (casaFoco ? homeName(casaFoco) : `Chofer ${foco}`);
  const trazados = useFocoTrazados(foco, paradasFoco, token);

  useEffect(() => {
    if (!leafletMap) return;
    const { L, map, extra: g } = leafletMap;
    const enFoco = foco == null ? null : { trazados: Object.values(trazados), paradas: paradasFoco, casa: casaFoco, chofer: choferFoco, nombre: nombreFoco };
    const puntos = dibujarFoco(L, g, enFoco, pick);
    if (foco == null) { encuadrado.current = null; return; }
    // Se encuadra una vez al entrar al foco, no en cada refresco.
    if (encuadrado.current !== foco && puntos.length) {
      encuadrado.current = foco;
      const encuadrar = () => (puntos.length === 1 ? map.setView(puntos[0], 14) : map.fitBounds(puntos, { padding: [30, 30] }));
      // Leaflet ignora un zoom pedido mientras anima otro (el de haber tocado
      // la fila un instante antes): se espera a que termine.
      if (map._animatingZoom) map.once("zoomend", encuadrar); else encuadrar();
    }
  }, [leafletMap, foco, paradasFoco, trazados, casaFoco, choferFoco, nombreFoco, pick]);

  useEffect(() => {
    if (leafletMap) dibujarMarca(leafletMap.L, leafletMap.extra, ubicar(sel));
  }, [leafletMap, sel, ubicar]);

  const resumen = useMemo(() => resumenChoferes(choferes), [choferes]);
  const colectas = useMemo(() => stopCounts(stops, "PICKUP"), [stops]);
  const repartos = useMemo(() => stopCounts(stops, "DELIVERY"), [stops]);
  const porEstado = { PENDING: colectas.PENDING + repartos.PENDING, DONE: colectas.DONE + repartos.DONE, PROBLEM: colectas.PROBLEM + repartos.PROBLEM };
  // Con un filtro de choferes, «visibles/total»; los Turbos, solo si se
  // sabe (sin pedirlos o con error, «…» y no un 0 engañoso).
  const conteo: Partial<Record<LayerKey, number | string>> = {
    drivers: prefs.driverFilter === "ALL" ? choferes.length : `${choferesVisibles.length}/${choferes.length}`,
    ...(turbosVivos.data && !turbosVivos.error ? { turbo: turbos.filter((t) => !t.terminal).length } : {}),
    ...(capas ? { pickups: colectas.total, deliveries: repartos.total, warehouses: capas.warehouses.length, stores: capas.stores.length, zones: capas.zones.length } : {}),
    ...(domicilios ? { homes: domicilios.length } : {}),
  };
  const capasDisponibles = LAYERS.filter((c) => (c.key !== "homes" || domicilios !== false) && (c.key !== "turbo" || verTurbo));
  const ocultas = LAYERS.filter((c) => !capasDisponibles.includes(c)).map((c) => c.key);
  const vistaActual = foco == null ? currentView(prefs, ocultas) : null;
  const vistas = VIEWS.filter((v) => v.key !== "turbo" || verTurbo);
  const choferesConParadas = useMemo(() => new Set(stops.map((s) => s.driver_id).filter((x): x is number => x != null)), [stops]);
  const conParadasHoy = useMemo(() => stopDrivers(stops), [stops]);

  const ordenados = useMemo(() => {
    const q = normalize(filtros.q.trim());
    return ordenarChoferes(choferesVisibles.filter((c) => !q || normalize(`${nombreChofer(c)} ${c.username} ${c.vehiculo ?? ""}`).includes(q)));
  }, [choferesVisibles, filtros.q]);
  const conteoFiltro: Record<DriverFilter, number> = {
    ALL: choferes.length,
    TURBO: choferes.filter(llevaTurbo).length,
    ENABLED: choferes.filter((c) => c.turbo_enabled === true || llevaTurbo(c)).length,
    CHOSEN: contarElegidos(choferes, prefs.chosen),
  };
  // El filtro de choferes vale para el mapa aunque la lista muestre otra
  // cosa (y queda guardado): sin este aviso, «faltaban» choferes en el mapa.
  const filtroActivo = DRIVER_FILTERS.find((f) => f.key === prefs.driverFilter && f.key !== "ALL");
  const textoFiltro = prefs.driverFilter === "CHOSEN"
    ? `${conteoFiltro.CHOSEN} de ${prefs.chosen.length} en el mapa`
    : `${choferesVisibles.length} de ${choferes.length} en el mapa`;

  const filas = useMemo(
    () => (foco != null ? paradasFoco.map(stopRow) : prefs.view === "drivers" ? [] : rowsFor(prefs.view, capas, homes, filtros, turbos)),
    [foco, paradasFoco, prefs.view, capas, homes, filtros, turbos]
  );

  const avisarAlChofer = async (c: Chofer) => {
    setAvisando(c.driver_id);
    try {
      const json = await requestJson(`/v2/drivers/${c.driver_id}/ping`, { token, method: "POST", body: {} });
      setAviso(json.sent
        ? { driverId: c.driver_id, texto: "Aviso enviado al teléfono del chofer", ok: true }
        : { driverId: c.driver_id, texto: `No tiene la app registrada para recibir avisos${json.phone ? `: llamalo al ${json.phone}` : ""}`, ok: false });
    } catch (e: any) {
      setAviso({ driverId: c.driver_id, texto: e.message, ok: false });
    } finally { setAvisando(null); }
  };

  const esSel = (kind: LayerKey, id: number) => sel?.kind === kind && sel.id === id;
  const esParadas = foco == null && (prefs.view === "pickups" || prefs.view === "deliveries");
  const kmFoco = Object.values(trazados).reduce((n, t) => (t.km != null ? n + t.km : n), 0);
  const hechasFoco = paradasFoco.filter((s) => s.state === "DONE").length;
  const problemasFoco = paradasFoco.filter((s) => s.state === "PROBLEM").length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Choferes en vivo</h2>
            <p style={subtitleStyle}>
              La última posición de cada chofer. Lo que hay que mirar primero es el gris: hace más de {SIN_SENAL_MIN} minutos que no reporta.
              En «Capas» se elige qué se ve en el mapa; a la derecha, la lista de lo que quieras (choferes, colectas, tiendas…). Tocá algo en el mapa o en la lista para ver su ficha.
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <Button variant={soloActivos ? "primary" : "secondary"} onClick={() => setSoloActivos((v) => !v)}>
              {soloActivos ? "Mostrando solo los que reportan" : "Mostrar solo los que reportan"}
            </Button>
            <Button variant="secondary" onClick={() => { ubicaciones.reload(); capasVivas.reload(); }}>Actualizar</Button>
          </div>
        </div>

        <KpiStrip resumen={resumen} actualizado={ubicaciones.updatedAt} refrescoSeg={REFRESCO_MS / 1000} />
        {ubicaciones.error ? <div style={{ marginTop: "12px", color: "var(--danger)" }}>{ubicaciones.error}</div> : null}
      </Card>

      {/* En el celular, mapa y lista uno abajo del otro: a dos columnas, a
          375 px el mapa quedaba de unos pocos píxeles. */}
      <div data-testid="mapa-y-lista" style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(300px, 1fr)", gap: "20px" }}>
        <Card>
          <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", marginBottom: "10px" }}>
            <LayersMenu capas={capasDisponibles} prefs={prefs} conteo={conteo} porEstado={porEstado} abierto={menuAbierto} onAbierto={setMenuAbierto}
              onCapa={alternarCapa} onEstado={alternarEstado} onAgrupar={() => cambiarPrefs((p) => ({ ...p, cluster: !p.cluster }))} />
            <div role="group" aria-label="Vistas" style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {vistas.map((v) => (
                <button key={v.key} type="button" aria-pressed={vistaActual === v.key} style={capaStyle(vistaActual === v.key)} onClick={() => aplicarVista(v.key)}>
                  {v.label}
                </button>
              ))}
            </div>
            {foco == null && filtroActivo ? (
              <span role="status" aria-label="Filtro de choferes en el mapa" style={{ ...chipFocoStyle, ...(choferesVisibles.length ? {} : { border: "1px solid var(--danger)" }) }}>
                Choferes: {filtroActivo.label} ({textoFiltro})
                <button type="button" style={linkBtnStyle} onClick={() => elegirFiltro("ALL")}>ver todos</button>
              </span>
            ) : null}
            {foco != null ? (
              <span style={chipFocoStyle}>
                Viendo el recorrido de {nombreFoco}
                <button type="button" aria-label="Salir del recorrido" onClick={() => setFoco(null)} style={{ ...cerrarStyle, fontSize: "15px", padding: 0 }}>×</button>
              </span>
            ) : null}
            {colectas.unlocated + repartos.unlocated ? <span style={{ ...smallMutedStyle, color: "var(--warning, #d29922)" }}>{colectas.unlocated + repartos.unlocated} paradas sin ubicación (no se ven)</span> : null}
          </div>
          {capasVivas.error ? <div style={{ ...smallMutedStyle, color: "var(--danger)", marginBottom: "8px" }}>No se pudieron cargar las capas: {capasVivas.error}</div> : null}
          {pedirTurbos && turbosVivos.error ? <div style={{ ...smallMutedStyle, color: "var(--danger)", marginBottom: "8px" }}>No se pudieron cargar los Turbos: {turbosVivos.error}</div> : null}
          <div ref={contenedorRef} style={{ height: "600px", borderRadius: "12px", overflow: "hidden", background: "var(--surface-2, #0c0e12)" }}>
            {!leaflet ? <div style={{ ...smallMutedStyle, padding: "16px" }}>Cargando el mapa…</div> : null}
          </div>
          <Legend enFoco={foco != null} agrupado={prefs.cluster} turbos={verTurbo && prefs.visible.turbo} />
        </Card>

        <Card>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
            <span style={{ ...smallMutedStyle, fontWeight: 600 }}>Ver:</span>
            <select aria-label="Ver" style={{ ...selectStyle, flex: 1 }} value={foco != null ? "foco" : prefs.view}
              onChange={(e) => e.target.value !== "foco" && elegirVista(e.target.value as LayerKey)}>
              {foco != null ? <option value="foco">Recorrido de {nombreFoco}</option> : null}
              {capasDisponibles.map((c) => <option key={c.key} value={c.key}>{c.label}{conteo[c.key] != null ? ` (${conteo[c.key]})` : ""}</option>)}
            </select>
          </div>

          {foco == null && prefs.view === "drivers" ? (
            <div role="group" aria-label="Filtro de choferes" style={{ display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center", marginBottom: "8px" }}>
              {DRIVER_FILTERS.map((f) => (
                <button key={f.key} type="button" aria-pressed={prefs.driverFilter === f.key} style={capaStyle(prefs.driverFilter === f.key)} onClick={() => elegirFiltro(f.key)}>
                  {f.label} <span style={{ ...smallMutedStyle, fontWeight: 400 }}>{conteoFiltro[f.key]}</span>
                </button>
              ))}
              <span style={{ ...smallMutedStyle, width: "100%" }}>
                En el mapa se ven los de la lista. Tildá «elegir» en cada chofer para armar tu grupo, o elegí de una vez:{" "}
                <button type="button" style={linkBtnStyle} onClick={() => elegirVarios(choferes.filter(llevaTurbo).map((c) => c.driver_id))}>los que llevan Turbo</button>
                {" · "}
                <button type="button" style={linkBtnStyle} onClick={() => elegirVarios(choferes.filter((c) => c.turbo_enabled || llevaTurbo(c)).map((c) => c.driver_id))}>los habilitados</button>
                {prefs.chosen.length ? <>{" · "}<button type="button" style={linkBtnStyle} onClick={() => cambiarPrefs((p) => ({ ...p, chosen: [], driverFilter: "ALL" }))}>quitar la elección</button></> : null}
              </span>
            </div>
          ) : null}

          {foco == null ? (
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
              <input aria-label="Buscar en la lista" style={searchStyle} placeholder="Buscar…" value={filtros.q} onChange={(e) => setFiltros((f) => ({ ...f, q: e.target.value }))} />
              {esParadas ? (
                <>
                  <select aria-label="Estado" style={selectStyle} value={filtros.state} onChange={(e) => setFiltros((f) => ({ ...f, state: e.target.value as ListFilters["state"] }))}>
                    <option value="ALL">Todos los estados</option>
                    {(Object.keys(STATE_LABEL) as StopState[]).map((s) => <option key={s} value={s}>{STATE_LABEL[s]}</option>)}
                  </select>
                  <select aria-label="Chofer" style={selectStyle} value={String(filtros.driverId)} onChange={(e) => setFiltros((f) => ({ ...f, driverId: e.target.value === "ALL" ? "ALL" : Number(e.target.value) }))}>
                    <option value="ALL">Todos los choferes</option>
                    {conParadasHoy.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </>
              ) : null}
              {prefs.view === "stores" ? (
                <select aria-label="Colecta hoy" style={selectStyle} value={filtros.storeToday} onChange={(e) => setFiltros((f) => ({ ...f, storeToday: e.target.value as ListFilters["storeToday"] }))}>
                  <option value="ALL">Todas</option>
                  <option value="WITH">Con colecta hoy</option>
                  <option value="WITHOUT">Sin colecta hoy</option>
                </select>
              ) : null}
            </div>
          ) : (
            <div style={{ ...smallMutedStyle, marginBottom: "10px" }}>
              {paradasFoco.length} parada{paradasFoco.length === 1 ? "" : "s"} · {hechasFoco} hecha{hechasFoco === 1 ? "" : "s"}{problemasFoco ? ` · ${problemasFoco} con problema` : ""}{kmFoco ? ` · ${fmtKm(kmFoco)} por calles` : ""}
              {" · "}<button type="button" style={linkBtnStyle} onClick={() => setFoco(null)}>salir</button>
            </div>
          )}

          {sel ? (
            <Ficha sel={sel} foco={foco} capas={capas} stops={stops} choferes={choferes} homes={homes} turbos={turbos} choferesConParadas={choferesConParadas}
              onVerRecorrido={verRecorrido} onIr={(href) => navigate(href)} onCerrar={() => setSel(null)} />
          ) : null}

          <div ref={listaRef} style={{ maxHeight: "560px", overflowY: "auto" }}>
            {foco == null && prefs.view === "drivers" ? (
              ordenados.length === 0
                ? <div style={smallMutedStyle}>{!choferes.length ? "Ningún chofer reportó todavía." : prefs.driverFilter === "CHOSEN" && !prefs.chosen.length ? "Todavía no elegiste a nadie: pasá a «Todos» y tildá «elegir» en los choferes que querés seguir." : prefs.driverFilter !== "ALL" && !choferesVisibles.length ? "Ningún chofer con ese filtro ahora." : "Ningún chofer coincide con la búsqueda."}</div>
                : ordenados.map((c) => (
                  <DriverRow key={c.driver_id} chofer={c} elegido={esSel("drivers", c.driver_id)} avisando={avisando === c.driver_id}
                    marcado={prefs.chosen.includes(c.driver_id)} onMarcar={() => alternarElegido(c.driver_id)}
                    aviso={aviso && aviso.driverId === c.driver_id ? aviso : null} tieneParadas={choferesConParadas.has(c.driver_id)}
                    onElegir={() => seleccionar({ kind: "drivers", id: c.driver_id }, "lista")} onAvisar={() => avisarAlChofer(c)} onVerRecorrido={() => verRecorrido(c.driver_id)} />
                ))
            ) : filas.length === 0 ? (
              <div style={smallMutedStyle}>{capas ? "No hay nada que coincida." : "Cargando…"}</div>
            ) : (
              filas.map((r) => (
                <div key={`${r.kind}:${r.id}`} data-fila={`${r.kind}:${r.id}`} style={{ ...filaStyle, background: esSel(r.kind, r.id) ? "rgba(255,255,255,0.06)" : "transparent", opacity: r.lat == null ? 0.6 : 1 }}
                  onClick={() => seleccionar({ kind: r.kind, id: r.id }, "lista")}>
                  <i style={punto(r.color)} />
                  <div>
                    {/* Botón para llegar con el teclado; el clic sube a la fila. */}
                    <button type="button" style={filaTituloStyle} aria-current={esSel(r.kind, r.id) ? "true" : undefined}>{r.title}</button>
                    <div style={smallMutedStyle}>{r.subtitle}{r.lat == null ? " · sin ubicación" : ""}</div>
                  </div>
                  <span />
                </div>
              ))
            )}
          </div>
        </Card>
      </div>

      <GpsFranjaCard token={token} />
    </div>
  );
}
